<template>
    <FilterFlyout
        :name="$t('results.filtering.monthlyPaymentRange')"
        :isActive="filterActive"
        :isModified="filterModified"
        @save="save"
        @clear="clear"
    >
        <div class="monthly-payment-range">
            <div class="slider-input" ref="slider"></div>

            <div class="input-row">
                <div class="input-wrapper">
                    <input
                        id="payment_range_min"
                        class="text-input filled"
                        name="payment_range_min"
                        :value="editingLoanAmountRange.min"
                        @change="paymentInputChanged(0, $event.target.value)"
                    />

                    <FloatingLabel :label="$t('results.filtering.min')" />
                </div>

                <div class="line"></div>

                <div class="input-wrapper">
                    <input
                        id="payment_range_max"
                        class="text-input filled"
                        name="payment_range_max"
                        :value="editingLoanAmountRange.max"
                        @change="paymentInputChanged(1, $event.target.value)"
                    />

                    <FloatingLabel :label="$t('results.filtering.max')" />
                </div>
            </div>
        </div>
    </FilterFlyout>
</template>

<script>
import noUiSlider from 'nouislider'
import wNumb from 'wnumb'
import { useStore, mapGetters } from 'vuex'
import useFilterBase from '@js/composables/filterBase.js'

// lodash components
import cloneDeep from 'lodash/cloneDeep'

export default {
    setup() {
        const store = useStore()

        return {
            store,
            ...useFilterBase(store),
        }
    },

    data() {
        return {
            slider: null,
            editingLoanAmountRange: null,
            activeLoanAmountRange: null,
            moneyFormat: wNumb({
                decimals: 0,
                prefix: this.$t('currency'),
                thousand: ',',
            }),
        }
    },

    created() {
        this.editingLoanAmountRange = cloneDeep(this.sourceLoanAmountRange)
        this.activeLoanAmountRange = cloneDeep(this.sourceLoanAmountRange)
    },

    mounted() {
        this.initSlider()

        this.filterState.save = this.save
        this.filterState.clear = this.clear
        this.filterState.filterItem = this.filterItem
        this.sliderUpdate = false
    },

    beforeUpdate() {
        if (this.incorrectSourceValues || this.sliderUpdate === false) {
            this.initSlider()
            this.clear()
        }

        this.sliderUpdate = false
    },

    computed: {
        ...mapGetters(['offerResultDataFilters']),
        filterModified() {
            this.filterState.isModified =
                JSON.stringify(this.activeLoanAmountRange) !== JSON.stringify(this.editingLoanAmountRangeRaw)
            return this.filterState.isModified
        },
        filterActive() {
            this.filterState.isActive =
                JSON.stringify(this.activeLoanAmountRange) !== JSON.stringify(this.sourceLoanAmountRange)
            return this.filterState.isActive
        },
        editingLoanAmountRangeRaw() {
            // the edited values contain formatting to show correctly in the inputs. So when comparing, remove formatting
            return {
                min: this.moneyFormat.from(this.editingLoanAmountRange.min),
                max: this.moneyFormat.from(this.editingLoanAmountRange.max),
            }
        },
        editingLoanAmountRangeFormatted() {
            // the edited values contain formatting to show correctly in the inputs. So when comparing, remove formatting
            return {
                min: this.moneyFormat.to(this.editingLoanAmountRange.min),
                max: this.moneyFormat.to(this.editingLoanAmountRange.max),
            }
        },
        sourceLoanAmountRange() {
            return this.offerResultDataFilters.loan_amount
        },
        incorrectSourceValues() {
            return (
                this.activeLoanAmountRange.min < this.sourceLoanAmountRange.min ||
                this.activeLoanAmountRange.max > this.sourceLoanAmountRange.max
            )
        },
    },

    methods: {
        filterItem(offer) {
            // if it is a range, check if this offer is inside the range at all
            if (offer.type === 'alternative') {
                return true
            }

            if (offer.loan_amount.max) {
                return (
                    offer.loan_amount.min <= this.activeLoanAmountRange.max &&
                    offer.loan_amount.max >= this.activeLoanAmountRange.min
                )
            }

            return (
                offer.loan_amount.min >= this.activeLoanAmountRange.min &&
                offer.loan_amount.min <= this.activeLoanAmountRange.max
            )
        },
        initSlider() {
            const $slider = this.$refs.slider

            if (typeof $slider !== 'undefined') {
                if (this.slider === null) {
                    const options = {
                        connect: true,
                        format: this.moneyFormat,
                        range: {
                            min: this.sourceLoanAmountRange.min,
                            max: this.sourceLoanAmountRange.max,
                        },
                        start: [this.sourceLoanAmountRange.min, this.sourceLoanAmountRange.max],
                        step: 100,
                    }

                    this.slider = noUiSlider.create($slider, options)

                    // Update the min/max input values with the selected range
                    $slider.noUiSlider.on('update', (values, handle) => {
                        this.sliderUpdate = true
                        const value = values[handle]

                        if (handle) {
                            this.editingLoanAmountRange.max = value
                        } else {
                            this.editingLoanAmountRange.min = value
                        }
                    })
                } else {
                    // Update the slider options to avoid re-initialization
                    $slider.noUiSlider.updateOptions({
                        range: {
                            min: this.sourceLoanAmountRange.min,
                            max: this.sourceLoanAmountRange.max,
                        },
                        start: [this.sourceLoanAmountRange.min, this.sourceLoanAmountRange.max],
                    })
                }
            }
        },
        paymentInputChanged(handle, value) {
            this.slider.setHandle(handle, value)
        },
        save() {
            this.activeLoanAmountRange.min = this.editingLoanAmountRangeRaw.min
            this.activeLoanAmountRange.max = this.editingLoanAmountRangeRaw.max
            this.sliderUpdate = true

            this.trackEvent('event', {
                eventCategory: 'filtering',
                eventAction: 'submit',
                eventLabel: 'monthly-payment',
            })
        },
        clear() {
            this.editingLoanAmountRange.min = this.sourceLoanAmountRange.min
            this.editingLoanAmountRange.max = this.sourceLoanAmountRange.max

            this.editingLoanAmountRange = this.editingLoanAmountRangeFormatted

            this.paymentInputChanged(0, this.editingLoanAmountRange.min)
            this.paymentInputChanged(1, this.editingLoanAmountRange.max)

            this.save()
        },
    },
}
</script>

<style lang="scss">
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';
@import '~scss/settings/variables';
@import 'nouislider/dist/nouislider.css';

.monthly-payment-range {
    .noUi-horizontal {
        height: 8px;
        border: none;
        box-shadow: none;
        background: #e0e1e5;

        .noUi-handle {
            border-radius: 50%;
            box-shadow: none;
            width: 32px;
            height: 32px;
            top: -12px;

            &:before {
                height: 14px;
                width: 9px;
                background: transparent;
                left: 11px;
                top: 8px;
                border-left: 1px solid #e8e7e6;
                border-right: 1px solid #e8e7e6;
            }

            &:after {
                height: 14px;
                width: 1px;
                background: #e8e7e6;
                left: 15px;
                top: 8px;
            }
        }
    }

    .noUi-connect {
        background: var(--wizard-progress-bar-color);
    }

    .input-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 32px;

        .input-wrapper .floating-label > span {
            margin-right: 0;
        }

        input {
            min-width: 0;
        }

        .line {
            flex-shrink: 0;
            margin: 0 8px;
            width: 8px;
            height: 1px;
            background: #e0e1e5;
        }
    }
}
</style>
